import React from 'react';
import styled from 'styled-components';

// Helpers
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
dayjs.extend(calendar);

// Componnents
import Bid from './Bid';
import { useTranslation } from '~/Locale';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { useSelector } from 'react-redux';
import { globalInterestRate as globalInterestRateSelector } from '~/App/shared/selectors/finance';
import { Caption, Stack } from '@kvdbil/components';
import { inclVATtoExclVAT } from '~/helpers/currency';
import { inBusinessTimeZone, inTimeZone } from '~/helpers/timezone';
import { useDisplayTimeZone } from '~/App/shared/hooks/useDisplayTimeZone';
import { useStableNow } from '~/App/shared/hooks/useStableNow';

const BiddingList = styled.ul`
  display: flex;
  flex-direction: column;
`;

const StyledCaption = styled(Caption)`
  text-align: center;
`;

export interface BiddingHistoryProps {
  isLeading: boolean;
  isClosed: boolean;
  isReservationPriceReached: boolean;
  maxBidAmount: number;
  auction: ProcessedAuction;
  myBidderAlias: string;
}
export const BiddingHistory = ({
  auction,
  isClosed,
  isLeading,
  myBidderAlias,
  isReservationPriceReached,
  maxBidAmount
}: BiddingHistoryProps) => {
  const { t } = useTranslation();
  const displayTimeZone = useDisplayTimeZone();
  const stableNow = useStableNow();
  const interestRate = useSelector(globalInterestRateSelector);
  const vatExcludedBiddingAllowed =
    auction.activeAuction?.vatExcludedBiddingAllowed ?? false;

  return (
    <Stack>
      {vatExcludedBiddingAllowed && (
        <StyledCaption>
          {t('The bidding history below is shown exclusive of VAT')}
        </StyledCaption>
      )}
      <BiddingList>
        {auction.bids.map(({ type, amount, createdAt, bidderAlias }, index) => {
          const isMyBid = bidderAlias === myBidderAlias;
          const now = inBusinessTimeZone(stableNow).startOf('day');
          const diffDays = Math.abs(now.diff(createdAt, 'days'));
          const showDateFormat = diffDays >= 1;
          const time = showDateFormat
            ? inTimeZone(createdAt, displayTimeZone).format('D MMM HH:mm')
            : inTimeZone(createdAt, displayTimeZone).calendar(
                inTimeZone(stableNow, displayTimeZone)
              );

          let bidType = t('bid, %1$s', time);

          if (type === 'AUTO') {
            bidType = t('autobid, %1$s', time);
          }

          if (type === 'LIMIT_REACHED') {
            bidType = t('autobid %1$s - maxbid', time);
          }

          const typeLabel = isMyBid ? t('Your %1$s', bidType) : bidType;
          const isHighestBid = index === 0;

          return (
            <Bid
              key={`${amount}-${bidderAlias}`}
              amount={
                vatExcludedBiddingAllowed ? inclVATtoExclVAT(amount) : amount
              }
              isClosed={isClosed}
              reservationPriceReached={isReservationPriceReached}
              bidderAlias={bidderAlias || 'Unknown'}
              isMyBid={isMyBid}
              typeLabel={typeLabel}
              isHighestBid={isHighestBid}
              myMaxBid={
                vatExcludedBiddingAllowed
                  ? inclVATtoExclVAT(maxBidAmount)
                  : maxBidAmount
              }
              isLeading={isLeading}
              interestRate={interestRate}
            />
          );
        })}
      </BiddingList>
    </Stack>
  );
};

export default BiddingHistory;
